/**
 * Signed in, and in no company at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO PEOPLE ARRIVE HERE AND NEITHER IS BROKEN
 *
 * The first abandoned the sign-up wizard before a workspace existed and has
 * come back days later. The second had one company, deleted it, and the fifteen
 * days ran out. Both have a real account, a correct password and nowhere to go,
 * and until now both were dropped straight into a half-remembered form with no
 * explanation of why they were looking at it.
 *
 * So the screen says where they are in one sentence and offers the one action
 * that changes it. The wizard is still the thing that creates the company; this
 * is the door to it, and the difference between the two is the difference
 * between "your account is fine, you just have no workspace" and "why am I
 * filling in this form again".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND THE OTHER WAY IN IS NAMED
 *
 * Somebody joining a colleague's company does not create one, they wait for an
 * invitation. Saying so here stops the person who was told "you will get access
 * on Monday" from creating a second, empty company on Sunday night, which is a
 * mess that has to be cleaned up by hand afterwards.
 */

import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { Logo } from '@/components/ui/Logo';
import { useI18n } from '@/contexts/I18nContext';

export function NoCompany() {
  const { t, dir } = useI18n();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-svh flex-col bg-background" dir={dir}>
      <header className="px-6 py-5 sm:px-10">
        <Logo size={22} />
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-6 sm:px-10">
        <span
          className="flex size-11 items-center justify-center rounded-xl bg-muted text-muted-foreground"
          aria-hidden
        >
          <Building2 className="size-5" />
        </span>

        <h1 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl">
          {t('no_company_title')}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          {t('no_company_lede')}
        </p>

        <Button size="lg" className="mt-8 h-11 self-start" onClick={() => navigate('/onboarding')}>
          {t('no_company_cta')}
          <ArrowRight className="size-4 rtl:rotate-180" />
        </Button>

        <p className="mt-6 text-[13px] leading-relaxed text-muted-foreground">
          {t('no_company_invited')}
        </p>
      </main>
    </div>
  );
}
